import { useEffect, useRef, useState } from 'react';
import {
  sendSignalAPI,
  pollSignalsAPI,
  getRoomParticipantsAPI,
} from '../services/api';

const ICE_SERVERS = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

/**
 * WebRTC hook using PHP + MySQL signaling (no Node.js).
 * For each pair (user.id, otherUser.id):
 *   -> client with SMALLER user.id becomes initiator (sends offer).
 */
export const useWebRTC = ({ roomCode, user, onRecordingComplete }) => {
  const localVideoRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);

  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [joinTime, setJoinTime] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState([]); // { userId, stream }

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const peersRef = useRef({});        // userId -> RTCPeerConnection
  const lastSignalIdRef = useRef(0);  // last processed signal id

  useEffect(() => {
    let activeStream;
    let participantsTimer;
    let pollTimer;

    const createPeer = (otherUserId, stream) => {
      console.log('[WebRTC] createPeer ->', otherUserId);
      const peer = new RTCPeerConnection(ICE_SERVERS);

      stream.getTracks().forEach((t) => peer.addTrack(t, stream));

      peer.ontrack = (event) => {
        const [remoteStream] = event.streams;
        console.log('[WebRTC] ontrack from', otherUserId);
        setRemoteStreams((prev) => {
          const exist = prev.find((r) => r.userId === otherUserId);
          if (exist) return prev;
          return [...prev, { userId: otherUserId, stream: remoteStream }];
        });
      };

      peer.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('[WebRTC] send ICE to', otherUserId);
          sendSignalAPI(roomCode, user.id, otherUserId, 'candidate', event.candidate)
            .catch((err) => console.error('send candidate error', err));
        }
      };

      return peer;
    };

    const makeOffer = async (peer, otherUserId) => {
      try {
        console.log('[WebRTC] makeOffer ->', otherUserId);
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        await sendSignalAPI(roomCode, user.id, otherUserId, 'offer', peer.localDescription);
      } catch (err) {
        console.error('[WebRTC] makeOffer error', err);
      }
    };

    const start = async () => {
      try {
        console.log('[WebRTC] getting user media...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        activeStream = stream;
        setLocalStream(stream);
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        setJoinTime(Date.now());

        setParticipants([
          { id: user.id, name: user.name, joinedAt: new Date().toISOString() },
        ]);

        // 1) periodically refresh participants and create peers
        const refreshParticipants = async () => {
          try {
            const data = await getRoomParticipantsAPI(roomCode);
            // backend must return: [{ user_id, name, joined_at }, ...]
            const mapped = data.map((p) => ({
              id: p.user_id,
              name: p.name,
              joinedAt: p.joined_at,
            }));

            setParticipants(mapped);

            for (const p of mapped) {
              if (p.id === user.id) continue;
              if (!peersRef.current[p.id]) {
                const peer = createPeer(p.id, stream);
                peersRef.current[p.id] = peer;

                // initiator rule: smaller id sends offer
                if (user.id < p.id) {
                  await makeOffer(peer, p.id);
                }
              }
            }
          } catch (err) {
            console.error('participants error', err);
          }
        };

        await refreshParticipants();
        participantsTimer = setInterval(refreshParticipants, 3000);

        // 2) poll for new offers / answers / candidates
        const pollSignals = async () => {
          try {
            const data = await pollSignalsAPI(
              roomCode,
              user.id,
              lastSignalIdRef.current
            );
            const { signals, last_id } = data;
            if (signals && signals.length) {
              lastSignalIdRef.current = last_id;
            }
            for (const s of signals) {
              const otherUserId = s.from_user_id;
              const payload = JSON.parse(s.payload);
              let peer = peersRef.current[otherUserId];
              if (!peer && activeStream) {
                peer = createPeer(otherUserId, activeStream);
                peersRef.current[otherUserId] = peer;
              }
              if (!peer) continue;

              if (s.type === 'offer') {
                const desc = new RTCSessionDescription(payload);
                console.log('[WebRTC] OFFER from', otherUserId, 'state=', peer.signalingState);
                if (peer.signalingState !== 'stable') {
                  console.warn('[WebRTC] ignore offer in', peer.signalingState);
                  continue;
                }
                await peer.setRemoteDescription(desc);
                const answer = await peer.createAnswer();
                await peer.setLocalDescription(answer);
                await sendSignalAPI(
                  roomCode,
                  user.id,
                  otherUserId,
                  'answer',
                  peer.localDescription
                );
              } else if (s.type === 'answer') {
                const desc = new RTCSessionDescription(payload);
                console.log('[WebRTC] ANSWER from', otherUserId, 'state=', peer.signalingState);
                if (peer.signalingState !== 'have-local-offer') {
                  console.warn('[WebRTC] ignore answer in', peer.signalingState);
                  continue;
                }
                await peer.setRemoteDescription(desc);
              } else if (s.type === 'candidate') {
                console.log('[WebRTC] ICE from', otherUserId);
                try {
                  await peer.addIceCandidate(new RTCIceCandidate(payload));
                } catch (err) {
                  console.error('addIceCandidate error', err);
                }
              }
            }
          } catch (err) {
            console.error('poll error', err);
          }
        };

        pollTimer = setInterval(pollSignals, 1500);
      } catch (err) {
        console.error('Error accessing camera/mic', err);
        alert('Could not access camera/microphone. Check permissions.');
      }
    };

    start();

    return () => {
      console.log('[WebRTC] cleanup');
      if (activeStream) activeStream.getTracks().forEach((t) => t.stop());
      Object.values(peersRef.current).forEach((p) => p.close());
      peersRef.current = {};
      if (participantsTimer) clearInterval(participantsTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [roomCode, user]);

  // === camera / mic / screen share / recording ===

  const toggleCamera = () => {
    if (!localStream) return;
    localStream.getVideoTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setIsCameraOn(track.enabled);
    });
  };

  const toggleMic = () => {
    if (!localStream) return;
    localStream.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setIsMicOn(track.enabled);
    });
  };

  const toggleScreenShare = async () => {
    if (!navigator.mediaDevices.getDisplayMedia) {
      alert('Screen share is not supported in this browser.');
      return;
    }

    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];

        const [videoTrack] = localStream.getVideoTracks();
        localStream.removeTrack(videoTrack);
        videoTrack.stop();
        localStream.addTrack(screenTrack);

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = null;
          localVideoRef.current.srcObject = localStream;
        }

        setIsScreenSharing(true);

        Object.values(peersRef.current).forEach((peer) => {
          const senders = peer.getSenders().filter((s) => s.track?.kind === 'video');
          senders.forEach((sender) => sender.replaceTrack(screenTrack));
        });

        screenTrack.onended = async () => {
          const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
          const camTrack = camStream.getVideoTracks()[0];

          localStream.removeTrack(screenTrack);
          screenTrack.stop();
          localStream.addTrack(camTrack);
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
            localVideoRef.current.srcObject = localStream;
          }
          setIsScreenSharing(false);

          Object.values(peersRef.current).forEach((peer) => {
            const senders = peer.getSenders().filter((s) => s.track?.kind === 'video');
            senders.forEach((sender) => sender.replaceTrack(camTrack));
          });
        };
      } catch (err) {
        console.error('Screen share error', err);
      }
    } else {
      alert('Use browser/system UI to stop screen share.');
    }
  };

  const startRecording = () => {
    if (!localStream) {
      alert('No media stream available.');
      return;
    }
    recordedChunksRef.current = [];
    const mediaRecorder = new MediaRecorder(localStream, {
      mimeType: 'video/webm;codecs=vp9',
    });

    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, {
        type: 'video/webm',
      });
      const url = URL.createObjectURL(blob);

      const durationSeconds = Math.floor(
        (Date.now() - (joinTime || Date.now())) / 1000
      );

      if (onRecordingComplete) {
        onRecordingComplete({ blob, url, durationSeconds });
      }
    };

    mediaRecorder.start();
    console.log('[Recording] started');
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      console.log('[Recording] stopped');
    }
  };

  return {
    localVideoRef,
    participants,
    joinTime,
    remoteStreams,
    isCameraOn,
    isMicOn,
    isScreenSharing,
    toggleCamera,
    toggleMic,
    toggleScreenShare,
    startRecording,
    stopRecording,
  };
};
